/**
 * Vendor APIs may return `vendorAssessment` as a single object (legacy) or an array.
 * Collapse to one object so callers can keep using `vendorAssessment.businessStatus`, `.id`, etc.
 *
 * @param ideaIdHint When multiple assessments exist, prefer the entry whose `id` or `ideaId` matches (if any).
 */
export function normalizeVendorAssessmentInResponse<T = unknown>(
  data: T,
  ideaIdHint?: string | number | null,
  assessmentIdHint?: string | number | null
): T {
  if (data == null || typeof data !== "object") {
    return data;
  }
  const d = data as Record<string, unknown>;
  const va = d.vendorAssessment;
  // Legacy: single object, null, or missing — leave response unchanged (do not drop or wrap).
  if (va == null || typeof va !== "object" || !Array.isArray(va)) {
    return data;
  }
  if (va.length === 0) {
    return { ...d, vendorAssessment: undefined } as T;
  }
  let picked: unknown = va[0];
  if (va.length > 1 && ideaIdHint != null && String(ideaIdHint).trim() !== "") {
    const hint = String(ideaIdHint);
    const byHint = va.find((item: unknown) => {
      if (item == null || typeof item !== "object") return false;
      const o = item as Record<string, unknown>;
      return (assessmentIdHint && String(o.id ?? "") === String(assessmentIdHint)) || String(o.id ?? "") === hint || String(o.ideaId ?? "") === hint;
    });
    if (byHint != null) {
      picked = byHint;
    }
  }
  return { ...d, vendorAssessment: picked } as T;
}

/**
 * Picks contact data for Vendor Contact Information from getAllVendorAssessment-style payloads.
 * Uses `vendorContactInformation[0]` when present; otherwise maps nested `vendor` fields
 * (same idea as guest VendorForm when `vendorContactInformation` is missing).
 */
export function resolveVendorContactInformationFromAssessmentResponse(
  responseData: Record<string, unknown> | null | undefined
): Record<string, unknown> {
  if (responseData == null || typeof responseData !== "object") {
    return {};
  }

  const raw = responseData.vendorContactInformation;
  if (Array.isArray(raw) && raw.length > 0) {
    const first = raw[0];
    if (first != null && typeof first === "object" && !Array.isArray(first)) {
      return first as Record<string, unknown>;
    }
  }

  const vendor = responseData.vendor;
  if (vendor == null || typeof vendor !== "object" || Array.isArray(vendor)) {
    return {};
  }

  const vo = vendor as Record<string, unknown>;
  return {
    vendorName: vo.vendorCorporateName ?? "",
    corporateAddress: vo.vendorCorporateAddress ?? "",
    corporateCity: vo.vendorCity ?? "",
    corporateState: vo.vendorState ?? "",
    corporateCountry:
      vo.vendorCountry != null && vo.vendorCountry !== ""
        ? String(vo.vendorCountry)
        : "",
    corporateZip: vo.vendorZipCode ?? "",
  };
}

/**
 * Payload for `initializeIngredientDetailsForm` from getAllVendorAssessment.
 * Uses root-level `vendorName` when `vendorIngredientDetails` is missing, or when it exists
 * but has no non-empty `vendorName`.
 */
export function buildVendorIngredientDetailsInitFromAssessmentResponse(
  responseData: Record<string, unknown> | null | undefined
): Record<string, unknown> {
  if (responseData == null || typeof responseData !== "object") {
    return {};
  }
  const details = responseData.vendorIngredientDetails;
  const rootName = responseData.vendorName;
  const rootStr = rootName != null ? String(rootName).trim() : "";

  let base: Record<string, unknown>;
  if (details != null && typeof details === "object" && !Array.isArray(details)) {
    base = { ...(details as Record<string, unknown>) };
  } else {
    base = {};
  }

  const existing = base.vendorName;
  const existingStr = existing != null ? String(existing).trim() : "";
  if (existingStr === "" && rootStr !== "") {
    base.vendorName = rootName;
  }

  return base;
}

/**
 * Reads a single compliance string from API fragments that use different field names.
 * Guest getVendor often sends `complianceStatus`; save uses `ingredientCompliance`;
 * some payloads use `ingredientStatus`.
 */
export function resolveIngredientComplianceFromResponseFragment(
  fragment: unknown
): string | null {
  if (fragment == null || typeof fragment !== "object" || Array.isArray(fragment)) {
    return null;
  }
  const o = fragment as Record<string, unknown>;
  const keys = ["ingredientCompliance", "complianceStatus", "ingredientStatus"] as const;
  for (const k of keys) {
    const v = o[k];
    if (v != null && String(v).trim() !== "") {
      return String(v).trim();
    }
  }
  return null;
}

/**
 * True when a getGuestVendor (`/vendor/assessment/getVendor`) row includes a non-null `nda`
 * at the root or under `vendor` (matches how NDA is read elsewhere, e.g. `setNdaId`).
 */
export function isNdaPresentOnGuestVendorRow(row: unknown): boolean {
  if (row == null || typeof row !== "object" || Array.isArray(row)) {
    return false;
  }
  const r = row as Record<string, unknown>;
  if (r.nda != null) {
    return true;
  }
  const vendor = r.vendor;
  if (vendor != null && typeof vendor === "object" && !Array.isArray(vendor)) {
    return (vendor as Record<string, unknown>).nda != null;
  }
  return false;
}

/**
 * True when a vendor/assessment API `nda` object actually carries document data (id, fileId,
 * filename, or base64 data). Empty `{}` or null does not qualify — use this to drive UI such
 * as the "newer version documents" banner only when the server returned a real NDA.
 */
export function hasNdaValueInAssessmentApi(nda: unknown): boolean {
  if (nda == null || typeof nda !== "object" || Array.isArray(nda)) {
    return false;
  }
  const n = nda as Record<string, unknown>;
  const idOrFileId = n.id ?? n.fileId;
  if (idOrFileId != null && String(idOrFileId).trim() !== "") {
    return true;
  }
  const fn = n.filename;
  if (typeof fn === "string" && fn.trim() !== "") {
    return true;
  }
  const data = n.data;
  if (typeof data === "string" && data.trim() !== "") {
    return true;
  }
  return false;
}

/**
 * Resolves API IdValue-like shapes `{ id, value }` (and string/number ids) to a string for save payloads.
 * Returns "" when missing or invalid so callers can omit the field.
 */
export function resolveApiIdValueToString(value: unknown): string {
  if (value == null) {
    return "";
  }
  if (typeof value === "string" || typeof value === "number") {
    return String(value).trim();
  }
  if (typeof value === "object" && !Array.isArray(value)) {
    const id = (value as Record<string, unknown>).id;
    if (id == null) {
      return "";
    }
    return String(id).trim();
  }
  return "";
}
